import {
  BackPriority,
  Button,
  Dialog,
  type FocusRepair,
  initTvNavigation,
  setFocus,
  setScreenFocusDefault,
  useBackHandler,
  useFocusWatchdog,
  useKeyMap,
} from '@akpl/ui-tv';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { analytics } from './analytics';
import { APP_VERSION } from './api';
import { useBoot } from './boot/useBoot';
import { getBridge } from './bridge';
import { seedDevApiSession } from './dev/devApiSession';
import { seedDevSession } from './dev/devSession';
import { getI18n, initAppI18n } from './i18n';
import { queryClient } from './queryClient';
import { type ScreenName, useRouter } from './router';
import { devOverlayEnabled } from './shell/DevOverlay';
import { Shell } from './shell/Shell';
import styles from './shell/shell.module.css';
import { Stage } from './shell/Stage';
import { useStore } from './store';
import { useT } from './useT';

export { queryClient } from './queryClient';

const Gallery = lazy(() => import('./dev/Gallery'));

initTvNavigation({ debug: false, throttle: 125 });

const DEV_SCREENS: readonly ScreenName[] = ['home', 'search', 'profile', 'player'];

/** `?screen=gallery|home|search|profile|player` (dev builds only). */
export function devScreenParam(search = window.location.search): string | null {
  if (!import.meta.env.DEV) return null;
  return new URLSearchParams(search).get('screen');
}

/**
 * Dev builds only: `?tvToken=…&sid=…` signs in with a token from `POST /v1/dev/tv-token`
 * and opens the requested screen against the **real** local API (apps/tv/test-e2e).
 */
export function devApiSessionParams(search = window.location.search): {
  token: string;
  sid: string;
  songId?: string;
  itemId?: string;
  tone?: string;
} | null {
  if (!import.meta.env.DEV) return null;
  const q = new URLSearchParams(search);
  const token = q.get('tvToken');
  const sid = q.get('sid');
  if (!token || !sid) return null;
  return {
    token,
    sid,
    ...(q.get('songId') ? { songId: q.get('songId')! } : {}),
    ...(q.get('itemId') ? { itemId: q.get('itemId')! } : {}),
    ...(q.get('tone') ? { tone: q.get('tone')! } : {}),
  };
}

/** The button the remote lands on while the TV boots and when it cannot reach the API (UX-371). */
export const BOOT_RETRY_FOCUS = 'boot-retry';

/**
 * How long "Iniciando…" is allowed to say nothing else before the screen admits the wait is not
 * normal and puts «Reintentar» under the remote (UX-380).
 */
export const BOOT_SLOW_MS = 5_000;

/**
 * UX-373: the sentence the room is shown instead of `GET /config → 503`. Keyed by `AkpError`
 * code so every failure mode still says something different — which is the one job the raw
 * line was doing. Anything not listed falls back to "Algo salió mal".
 */
const BOOT_CAUSE = {
  upstream_unavailable: 'common:errors.upstream_unavailable',
  network_error: 'common:errors.network_error',
  timeout: 'common:errors.timeout',
  rate_limited: 'common:errors.rate_limited',
  internal: 'common:errors.internal',
  unauthorized: 'common:errors.unauthorized',
  forbidden: 'common:errors.forbidden',
  not_found: 'common:errors.not_found',
  contract_violation: 'common:errors.contract_violation',
} as const;

/**
 * Boot screen — and, when the API is unreachable, the one screen a room can actually meet
 * first (UX-371).
 *
 * It used to render "Reintentar" and stop there. The button is a `Button`, so it registered a
 * focusable with norigin, but **nothing ever focused it**: the watchdog that guarantees Carlos's
 * T1 rule ("never zero focused elements") lives in `Shell`, which only mounts once boot is
 * `ready`, and no screen had published a default for the boot screen either. The result was a
 * TV with `focusCount === 0` on which ArrowUp/Down/Left/Right, Enter and Back all did literally
 * nothing: a flaky living-room Wi-Fi at power-on produced a set that could only be recovered by
 * pulling the stick out of the wall. So the boot screen now carries the same three things every
 * other screen has — a registered default focusable, the watchdog, and a Back that does
 * something — and the exit dialog is rendered here rather than in the shell it never reaches.
 *
 * UX-373: the raw `GET /config → 503` is a developer's line on a 55" screen in a living room.
 * The cause is said in Spanish (from the error code) and the diagnostic is kept for `?debug=1`.
 */
function BootScreen({
  status,
  error,
  code,
  retry,
}: {
  status: string;
  error: string | null;
  code: string | null;
  retry: () => void;
}) {
  const { t } = useT();
  const isError = status === 'error';
  const [exitOpen, setExitOpen] = useState(false);
  const noteFocusRepair = useStore((s) => s.noteFocusRepair);
  const [debug] = useState(() =>
    devOverlayEnabled(typeof window === 'undefined' ? '' : window.location.search),
  );

  /**
   * UX-380: the wait itself is now a state the room can see. A hung API (as opposed to the fast
   * 503 UX-371 was tested against) kept "Iniciando…" on screen for 63 s with nothing to say and
   * nothing to press; after {@link BOOT_SLOW_MS} the screen says the wait is not normal and the
   * button on it becomes «Reintentar».
   */
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (isError) return undefined;
    const handle = setTimeout(() => setSlow(true), BOOT_SLOW_MS);
    return () => clearTimeout(handle);
  }, [isError, status]);

  const showRetry = isError || slow;
  /** A retry re-opens the whole boot window, so it re-opens its "still normal" part too. */
  const onRetry = () => {
    setSlow(false);
    retry();
  };

  /**
   * The watchdog's two inputs: which screen it is guarding, and where to put the focus when
   * there is none. Without this pair it diagnoses `no_focus` and has nowhere to send it.
   *
   * UX-380: this used to be gated on `status === 'error'`, so the *boot* screen — the one a
   * flaky living-room Wi-Fi actually shows first — had `focusCount 0` for as long as the API
   * took to give up. The default is published from the very first boot frame instead.
   */
  useEffect(() => {
    setScreenFocusDefault('boot', BOOT_RETRY_FOCUS);
    void setFocus(BOOT_RETRY_FOCUS);
  }, []);

  const onFocusRepair = useCallback(
    (repair: FocusRepair) => {
      noteFocusRepair(repair);
      analytics.track('focus.repair', {
        screen: repair.screen,
        fault: repair.fault,
        strategy: repair.strategy,
      });
    },
    [noteFocusRepair],
  );
  useFocusWatchdog({ onRepair: onFocusRepair });

  // Back: the shell's root handler is not mounted yet, so the chain is rooted here instead.
  // UX-380: also from the first boot frame — Escape and every Back spelling used to be dead for
  // the whole minute the TV spent waiting, which left the room with the power socket.
  useKeyMap({}, { dispatchBackChain: true });
  useBackHandler(
    () => {
      if (exitOpen) return false;
      setExitOpen(true);
      return true;
    },
    { priority: BackPriority.root },
  );

  // 503/504 → "Servicio no disponible…", a dead socket → "No hay conexión…".
  const cause = code
    ? t(BOOT_CAUSE[code as keyof typeof BOOT_CAUSE] ?? 'common:errors.generic')
    : null;

  return (
    <div
      className={styles.boot}
      data-testid="boot"
      data-status={status}
      data-slow={slow || undefined}
    >
      <div className={styles.bootDot} />
      <div className={styles.bootTitle}>{isError ? t('tv:boot.error') : t('tv:boot.starting')}</div>
      {isError ? <div className={styles.bootBody}>{t('tv:boot.errorHint')}</div> : null}
      {!isError && slow ? (
        <div className={styles.bootBody} role="status" data-testid="boot-slow">
          {t('tv:boot.slow')}
        </div>
      ) : null}
      {isError && cause ? (
        <div className={styles.bootCause} role="alert" data-testid="boot-cause">
          {cause}
        </div>
      ) : null}
      {isError && debug && error ? (
        <div className={styles.bootError} data-testid="boot-diagnostic">
          {error}
        </div>
      ) : null}
      {/*
        UX-380: one focusable, always mounted, so the remote always has something to aim at.
        «Salir» used to be what it offered while the boot was still within budget; it is gone
        (2026-10-05, owner request). Until the wait stops being normal — or the boot has failed —
        the control is invisible and inert, and then it becomes «Reintentar». Back still raises
        the exit dialog (above), so leaving the app is never lost.
      */}
      <div
        style={showRetry ? undefined : { opacity: 0 }}
        aria-hidden={showRetry ? undefined : true}
      >
        <Button
          focusKey={BOOT_RETRY_FOCUS}
          data-testid="boot-retry"
          variant="primary"
          onSelect={showRetry ? onRetry : () => undefined}
        >
          {t('common:actions.retry')}
        </Button>
      </div>
      <Dialog
        open={exitOpen}
        focusKey="dialog-exit"
        title={t('tv:home.exitTitle')}
        body={t('tv:home.exitBody')}
        onClose={() => setExitOpen(false)}
        actions={[
          {
            key: 'cancel',
            label: t('common:actions.cancel'),
            onSelect: () => setExitOpen(false),
          },
          {
            key: 'exit',
            label: t('tv:home.exitConfirm'),
            primary: true,
            onSelect: () => {
              setExitOpen(false);
              getBridge(APP_VERSION).bridge.exitApp();
            },
          },
        ]}
      />
    </div>
  );
}

function Booted() {
  const { status, error, code, retry } = useBoot();
  const devScreen = useMemo(() => devScreenParam(), []);

  const devApi = useMemo(() => devApiSessionParams(), []);

  useEffect(() => {
    if (status !== 'ready' || !devScreen) return;
    if (!DEV_SCREENS.includes(devScreen as ScreenName)) return;
    if (devApi) {
      // Real session from the local API (`POST /v1/dev/tv-token`), used by the e2e smoke.
      void seedDevApiSession(useStore, devApi).then(() => {
        useRouter.getState().reset('home');
        if (devScreen === 'player') {
          useRouter.getState().navigate('player', {
            ...(devApi.songId ? { songId: devApi.songId } : {}),
            ...(devApi.itemId ? { itemId: devApi.itemId } : {}),
            ...(devApi.tone ? { tone: devApi.tone } : {}),
          });
        } else if (devScreen !== 'home') {
          useRouter.getState().navigate(devScreen as ScreenName);
        }
      });
      return;
    }
    seedDevSession(useStore);
    useRouter.getState().reset('home');
    if (devScreen !== 'home') useRouter.getState().navigate(devScreen as ScreenName);
  }, [status, devScreen, devApi]);

  if (status !== 'ready') {
    return <BootScreen status={status} error={error} code={code} retry={retry} />;
  }
  return <Shell />;
}

export function App() {
  const gallery = devScreenParam() === 'gallery';
  // i18n has inline resources, so this resolves in the same tick; it just keeps every
  // `useTranslation` call after `initReactI18next` has an instance.
  const [i18nReady, setI18nReady] = useState(() => getI18n() !== null);
  useEffect(() => {
    if (i18nReady) return;
    void initAppI18n().then(() => setI18nReady(true));
  }, [i18nReady]);
  if (!i18nReady) return null;
  return (
    <QueryClientProvider client={queryClient}>
      <Stage>
        {gallery ? (
          <Suspense fallback={null}>
            <Gallery />
          </Suspense>
        ) : (
          <Booted />
        )}
      </Stage>
    </QueryClientProvider>
  );
}
